import { Dataset, PaginationRes, Project, ProjectTabsEnum, ProjectTypeEnum, ValidationRes } from '~/api/types';
import { v2Api } from '../v2Api';

export const PAGE_SIZE = 18;

export type CreateProjectBody = {
  name?: string;
  description?: string;
  operating_unit_id?: number;
  type?: ProjectTypeEnum;
  dataset_ids?: number[] | null;
  destination_dataset_ids?: number[] | null;
  wbs_code_id?: string;
};

const tags = ['project', 'member'] as const;

const projectApi = v2Api
  .enhanceEndpoints({
    addTagTypes: tags,
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getProjects: builder.infiniteQuery<
        { data: Project[]; pagination: PaginationRes },
        { tab?: ProjectTabsEnum; search?: string },
        number
      >({
        infiniteQueryOptions: {
          initialPageParam: 1,
          getNextPageParam: (lastPageParam) => {
            const currSize = lastPageParam.pagination.size;
            const currentPage = lastPageParam.pagination.page;
            const remainingSize = lastPageParam.pagination.total - currentPage * currSize;

            if (remainingSize <= 0) {
              return undefined;
            }

            return currentPage + 1;
          },
        },
        query: ({ pageParam, queryArg }) =>
          `projects?tab=${queryArg?.tab}&page=${pageParam}&size=${PAGE_SIZE}${queryArg?.search !== undefined ? `&search=${encodeURIComponent(queryArg?.search)}` : ''}`,
        providesTags: tags,
      }),
      getProject: builder.query<Project, { projectId: number }>({
        query: ({ projectId }) => `projects/${projectId}`,
        transformResponse: (response: { data: Project }) => response.data,
        providesTags: tags,
      }),
      createProject: builder.mutation<object, CreateProjectBody>({
        query: ({ ...body }) => {
          return {
            url: `projects`,
            method: 'POST',
            body,
          };
        },
        invalidatesTags: tags,
      }),
      editProject: builder.mutation<object, Partial<CreateProjectBody> & { projectId: number }>({
        query: ({ projectId, ...body }) => ({
          url: `projects/${projectId}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: tags,
      }),
      deactivateProject: builder.mutation<object, { projectId: number }>({
        query: ({ projectId }) => ({
          url: `projects/${projectId}/inactive`,
          method: 'POST',
        }),
        invalidatesTags: tags,
      }),
      getDatasetsForSelection: builder.query<Dataset[], { projectId: number; destinationType?: boolean }>({
        query: ({ projectId, destinationType = false }) => ({
          url: `projects/${projectId}/datasets/selection?destination_type=${destinationType}`,
          method: 'GET',
        }),
        transformResponse: (response: { data: Dataset[] }) => response.data,
        providesTags: tags,
      }),
      getValidateProjectName: builder.query<ValidationRes, { name: string }>({
        query: ({ name }) => `projects/validate-name?name=${encodeURIComponent(name)}`,
        transformResponse: (response: { data: ValidationRes }) => response.data,
        providesTags: tags,
      }),
    }),
  });

export default projectApi;
